<!-- Displays a box that provides an overview of the selected outbound/inbound journeys. Includes total price.
Pulls the selected outbound/inbound Journey Key from journey-store and calls GQL pricingBreakdown to show the price. -->
<script setup lang="ts">
import type { Ref } from "vue";
import { ref, watch, computed } from "vue";
import { useJourneyStore } from "@/stores/journey-store";
import gql from "graphql-tag";
import { useApolloClient } from "@vue/apollo-composable";
import _get from "lodash/get";
import { useRoute, useRouter } from "vue-router";
import PriceBreakdown from "@/components/3-Basket/PriceBreakdown.vue";
import type { IPriceBreakdown } from "@/models/graphql/PriceBreakdownResponse";
import JourneySummary from "@/components/3-Basket/JourneySummary.vue";
import JourneyPackages from "@/components/3-Basket/JourneyPackages.vue";
import TicketDetails from "@/components/3-Basket/TicketDetails.vue";
import { getFormattedDurationString } from "@/utilities/getFormattedDurationString";
import { proceedToReservationsPageFromSearchResultsPage } from "@/utilities/proceedToReservationsPageFromSearchResultsPage";
import _isEmpty from "lodash/isEmpty";
import { isMobile } from "@/utilities/IsMobileHelper";
import { JourneyType } from "@/models/JourneyType";
import CheaperFareAvailableNotification from "./CheaperFareAvailableNotification.vue";
import FirstClassTravelNotCompleteWarning from "./FirstClassTravelNotCompleteWarning.vue";

const { client } = useApolloClient();
const journeyStore = useJourneyStore();
const router = useRouter();
const td: Ref = ref(null);
const route = useRoute();
const isApiLoading = ref(false);
const pricingBreakdown: Ref<IPriceBreakdown | null> = ref(null);
const showPricingBreakdown = ref(false);
const chosenOutboundJourney = computed(() => journeyStore.getJourneyByJourneyKey(journeyStore.selectedOutboundJourney.journeyKey));
const chosenInboundJourney = computed(() => journeyStore.getJourneyByJourneyKey(journeyStore.selectedInboundJourney.journeyKey));
const journeySummaryComponent = ref({
    journeyKey: journeyStore.selectedOutboundJourney.journeyKey,
    show: false,
});
const ticketDetailsComponent = ref({
    restrictionDetails: null,
    ticketDetails: null,
    fareDetails: null,
    journeyType: "outbound",
});
const shouldContinueButtonBeDisabled = computed(() => {
    if (getTravelPolicyViolations().length > 0) {
        return true;
    }
    if (journeyStore.getPricingBreakdownError == "" && !_isEmpty(journeyStore.selectedOutboundFares)) {
        return false;
    }
    return true;
});

// Returns an array of textual explanation of the travel policy violations for the selected fare.
function getTravelPolicyViolations(): string[] {
    const travelPolicyViolations: string[] = [];

    // Get the JourneyFare for the selected outbound/inbound journey
    const journeyFare = journeyStore.getJourneyFareForSelectedJourneyKeys();

    if (!journeyFare) {
        return [];
    }

    // Loop over each Policy Violation key
    journeyFare.travelPolicyViolations.forEach((policyViolation) => {
        // Get the Policy Violation details
        policyViolation.value.fareCombinations.forEach((fareCombination) => {
            // If the selected out/inbound fare is in the fareCombination, then add the violation to be shown.
            if (journeyStore.currentJourneySearchResultsJourneyType === JourneyType.Return) {
                if (journeyStore.selectedOutboundFares.includes(fareCombination.outboundFareKey) && journeyStore.selectedInboundFares.includes(fareCombination.inboundFareKey)) {
                    travelPolicyViolations.push(policyViolation.value.violationDescription);
                }
            } else if (journeyStore.selectedOutboundFares.includes(fareCombination.outboundFareKey)) {
                travelPolicyViolations.push(policyViolation.value.violationDescription);
            }
        });
    });

    return travelPolicyViolations;
}

// The Ticket details component requires all data via props, so collect relevant data and show the dialog.
function showTicketDetailsComponent(journeyType: "outbound" | "inbound") {
    const fareKey = journeyType === "outbound" ? journeyStore.selectedOutboundFares[0] : journeyStore.selectedInboundFares[0];

    const fare = journeyStore.getFareObjectByFareKey(fareKey);
    ticketDetailsComponent.value.fareDetails = fare.value;
    const { ticketDetailsKey } = fare.value;
    const ticketRestrictionsKey = fare.value.restrictionCode;

    journeyStore.journeySearchResults.ticketRestrictionsCollection.find((restriction) => {
        if (restriction.key === ticketRestrictionsKey) {
            ticketDetailsComponent.value.restrictionDetails = restriction.value;
        }
    });

    journeyStore.journeySearchResults.ticketDetailsCollection.find((ticket) => {
        if (ticket.key === ticketDetailsKey) {
            ticketDetailsComponent.value.ticketDetails = ticket.value;
        }
    });

    ticketDetailsComponent.value.journeyType = journeyType;
    td.value.showDialog();
}

function getPricingBreakdown() {
    return new Promise(async (resolve, reject) => {
        try {
            isApiLoading.value = true;
            journeyStore.getPricingBreakdownError = "";

            let fares = journeyStore.getArrayOfAllFares();

            // Check if there are no journeys found
            if (!fares.length || !journeyStore.selectedOutboundJourney.journeyKey || (journeyStore.journeySearchResults && journeyStore.journeySearchResults.journeys && journeyStore.journeySearchResults.journeys.length === 0)) {
                // Reset pricingBreakdown to show £0.00
                pricingBreakdown.value = {
                    bookingAmountFormatted: "£0.00",
                    bookingFeeFormatted: "£0.00",
                    existingTrips: [],
                    totalPaymentAmountFormatted: "£0.00",
                    tripAmountFormatted: "£0.00",
                    willBookingFeeChangeDueToAddition: false,
                };

                isApiLoading.value = false;
                resolve(null);
                return;
            }

            // Ensure each fare is wrapped in quotes
            fares = fares.map((fare) => `"${fare}"`);

            const result = await client.query({
                query: gql`
                        query pricingBreakdown {
                            pricingBreakdown(
                                searchId: "${journeyStore.journeySearchResults?.searchId}"
                                outboundJourneyKey: "${journeyStore.selectedOutboundJourney.journeyKey}"
                                ${_get(journeyStore, "selectedInboundJourney.journeyKey", false) ? `inboundJourneyKey: "${journeyStore.selectedInboundJourney.journeyKey}"` : ""}
                                fareKeys: [${fares}]
                            ) {
                                bookingAmountFormatted
                                bookingFeeFormatted
                                existingTrips {
                                    amount
                                    inboundDate
                                    isReturn
                                    outboundDate
                                    summary
                                }
                                totalPaymentAmountFormatted
                                tripAmountFormatted
                                willBookingFeeChangeDueToAddition
                            }
                        }`,
            });

            pricingBreakdown.value = result.data.pricingBreakdown;

            isApiLoading.value = false;

            resolve(null);
            return;
        } catch (error) {
            console.error(error);
            isApiLoading.value = false;
            if (_get(error, "cause.result.errors[0].message", false)) {
                journeyStore.getPricingBreakdownError = error.cause.result.errors[0].message;
            } else {
                journeyStore.getPricingBreakdownError = "Uknown error. Please try again.";
            }
            reject();
        }
    });
}

// Continue button click handler. On mobile, we may be need to show the inbound journey screen before proceeding to reservations.
function onContinueButtonClick() {
    if (isMobile()) {
        if (journeyStore.showOutboundJourneySelectorColumn && journeyStore.currentJourneySearchResultsJourneyType === JourneyType.Return) {
            journeyStore.showOutboundJourneySelectorColumn = false;
            journeyStore.showInboundJourneySelectorColumn = true;
        } else {
            proceedToReservationsPageFromSearchResultsPage(router, route);
        }
    } else {
        proceedToReservationsPageFromSearchResultsPage(router, route);
    }
    window.scrollTo(0, 0);
}

// Return the percentage of the total outbound journey that is in first class as an integer. Note that if the selected journey option by the user is NOT first class, then this function will return false.
function percentageOfOutboundTravelInFirstClass(): number | false {
    if (journeyStore.selectedOutboundJourney?.fareClass !== "first") {
        return false;
    }
    const outboundJourneyKey = journeyStore.selectedOutboundJourney.journeyKey;
    if (!outboundJourneyKey) {
        return false;
    }
    const outboundJourney = journeyStore.getJourneyByJourneyKey(outboundJourneyKey);
    if (!outboundJourney) {
        return false;
    }
    const percentage = outboundJourney.firstClassCoverage;
    return percentage;
}

// Return the percentage of the total inbound journey that is in first class as an integer. Note that if the selected journey option by the user is NOT first class, then this function will return false.
function percentageOfInboundTravelInFirstClass(): number | false {
    if (journeyStore.selectedInboundJourney?.fareClass !== "first") {
        return false;
    }
    const inboundJourneyKey = journeyStore.selectedInboundJourney.journeyKey;
    if (!inboundJourneyKey) {
        return false;
    }
    const inboundJourney = journeyStore.getJourneyByJourneyKey(inboundJourneyKey);
    if (!inboundJourney) {
        return false;
    }
    const percentage = inboundJourney.firstClassCoverage;
    return percentage;
}

function isRailcardOnSelectedFares() {
    const outboundRailcards = journeyStore.selectedOutboundFares.some((fare) => {
        const fareObject = journeyStore.getFareObjectByFareKey(fare);
        return fareObject.value.railcards;
    });
    if (journeyStore.query.journeyType === JourneyType.Return) {
        const inboundRailcards = journeyStore.selectedInboundFares.some((fare) => {
            const fareObject = journeyStore.getFareObjectByFareKey(fare);
            return fareObject.value.railcards;
        });
        return outboundRailcards || inboundRailcards;
    }
    return outboundRailcards;
}

function getRailcardName() {
    let outboundRailcardNames = journeyStore.selectedOutboundFares.map((fare) => {
        const fareObject = journeyStore.getFareObjectByFareKey(fare);
        if (!fareObject.value.railcards) {
            return null;
        }
        return fareObject.value?.railcards[0]?.name;
    });
    outboundRailcardNames = outboundRailcardNames.filter((item) => item !== null);

    if (journeyStore.query.journeyType === JourneyType.Return) {
        let inboundRailcardNames = journeyStore.selectedInboundFares.map((fare) => {
            const fareObject = journeyStore.getFareObjectByFareKey(fare);
            if (!fareObject.value.railcards) {
                return null;
            }
            return fareObject.value?.railcards[0]?.name;
        });
        inboundRailcardNames = inboundRailcardNames.filter((item) => item !== null);

        // It is possible to have a railcard applied on one portion of the journey and not the other
        if (inboundRailcardNames.length === 0 && outboundRailcardNames.length > 0) {
            return `${outboundRailcardNames[0]}`;
        }
        if (inboundRailcardNames.length > 0 && outboundRailcardNames.length === 0) {
            return `${inboundRailcardNames[0]}`;
        }
    }
    return outboundRailcardNames[0];
}

watch(
    [() => journeyStore.selectedInboundFares, () => journeyStore.selectedOutboundFares, () => journeyStore.selectedOutboundJourney, () => journeyStore.selectedInboundJourney],
    async () => {
        // Check if there are any journeys found
        if (journeyStore.journeySearchResults && journeyStore.journeySearchResults.journeys && journeyStore.journeySearchResults.journeys.length === 0) {
            // Reset pricingBreakdown to show £0.00
            pricingBreakdown.value = {
                bookingAmountFormatted: "£0.00",
                bookingFeeFormatted: "£0.00",
                existingTrips: [],
                totalPaymentAmountFormatted: "£0.00",
                tripAmountFormatted: "£0.00",
                willBookingFeeChangeDueToAddition: false,
            };
        } else {
            await getPricingBreakdown();
        }
    },
    { immediate: true }
);
</script>
<template>
    <div class="journey-selection-overview ml-8">
        <CheaperFareAvailableNotification v-if="!isMobile()" @click="journeyStore.showOtherReturnFaresModal = true" class="mb-4"></CheaperFareAvailableNotification>

        <v-card class="overview elevation-1 rounded-lg">
            <v-card-text>
                <!-- MOBILE -->
                <template v-if="isMobile()">
                    <div class="total-price-wrapper">
                        <v-skeleton-loader v-if="isApiLoading || !pricingBreakdown?.totalPaymentAmountFormatted" type="text" class="w-100"></v-skeleton-loader>
                        <template v-else>
                            <span>Total</span><span>{{ pricingBreakdown?.totalPaymentAmountFormatted }} <span style="font-size: 0.8rem; font-weight: 300">inc. fee</span></span>
                        </template>
                    </div>

                    <p v-if="isRailcardOnSelectedFares()" class="railcard-discount-applied-text-mobile">Includes {{ getRailcardName() }} discount</p>

                    <div class="mobile-links my-1">
                        <p @click="showPricingBreakdown = true" class="text-decoration-underline">Pricing breakdown</p>

                        <a @click="journeyStore.showOtherReturnFaresModal = true" class="text-decoration-underline">View other fares</a>
                    </div>

                    <FirstClassTravelNotCompleteWarning :percentage="percentageOfOutboundTravelInFirstClass()" type="outbound"></FirstClassTravelNotCompleteWarning>
                    <FirstClassTravelNotCompleteWarning :percentage="percentageOfInboundTravelInFirstClass()" type="inbound"></FirstClassTravelNotCompleteWarning>

                    <div class="text-center mt-2">
                        <v-btn class="mb-1 mx-auto button text-white" :loading="isApiLoading" @click="onContinueButtonClick()" size="x-large" rounded="lg" block :disabled="shouldContinueButtonBeDisabled">Continue</v-btn>
                    </div>

                    <v-alert type="error" v-if="getTravelPolicyViolations().length > 0" class="mb-1 mt-2">
                        Selection is not permitted due to the following policies:
                        <template v-if="getTravelPolicyViolations().length > 0">
                            <ul>
                                <li v-for="(violation, index) in getTravelPolicyViolations()" :key="index">{{ violation }}</li>
                            </ul>
                        </template>
                        <template v-else>None</template>
                    </v-alert>

                    <v-alert :text="journeyStore.getPricingBreakdownError" type="error" v-if="journeyStore.getPricingBreakdownError !== ''" class="mb-3"></v-alert>
                </template>

                <!-- DESKTOP -->
                <template v-else>
                    <div class="total-price-wrapper">
                        <v-skeleton-loader v-if="isApiLoading || !pricingBreakdown?.totalPaymentAmountFormatted" type="text" class="w-100"></v-skeleton-loader>
                        <template v-else>
                            <span>Total</span><span>{{ pricingBreakdown?.totalPaymentAmountFormatted }}</span>
                        </template>
                    </div>

                    <p v-if="isRailcardOnSelectedFares()" class="railcard-discount-applied-text">Includes {{ getRailcardName() }} discount</p>

                    <v-alert type="error" v-if="getTravelPolicyViolations().length > 0" class="mb-1 mt-2">
                        Selection is not permitted due to the following policies:
                        <template v-if="getTravelPolicyViolations().length > 0">
                            <ul>
                                <li v-for="(violation, index) in getTravelPolicyViolations()" :key="index">{{ violation }}</li>
                            </ul>
                        </template>
                        <template v-else>None</template>
                    </v-alert>

                    <div class="text-center mt-5">
                        <v-btn class="mb-3 mx-auto button text-white" :loading="isApiLoading" @click="onContinueButtonClick()" size="x-large" rounded="lg" block :disabled="shouldContinueButtonBeDisabled">Continue</v-btn>
                    </div>

                    <v-alert :text="journeyStore.getPricingBreakdownError" type="error" v-if="journeyStore.getPricingBreakdownError !== ''" class="mb-3"></v-alert>

                    <v-skeleton-loader v-if="isApiLoading" class="mb-3" type="text"></v-skeleton-loader>
                    <p v-else class="d-block text-center text-caption font-weight-light">Total includes ticket price plus a small {{ pricingBreakdown?.bookingFeeFormatted }} fee</p>

                    <p @click="showPricingBreakdown = true" class="pricing-breakdown-link mt-1">Pricing breakdown</p>

                    <v-divider class="mt-5 mb-3"></v-divider>

                    <v-skeleton-loader v-if="isApiLoading" class="mb-3" type="paragraph"></v-skeleton-loader>
                    <div v-else class="mb-5 ticket-selection-details">
                        <div v-if="chosenOutboundJourney">
                            <div class="d-flex justify-start ga-1 mb-1">
                                <v-icon style="opacity: 0.8">mdi-arrow-right-box</v-icon>

                                <p>{{ chosenOutboundJourney?.originName ?? "N/A" }} to {{ chosenOutboundJourney?.destinationName ?? "N/A" }}</p>
                            </div>

                            <div class="d-flex justify-space-between flex-wrap">
                                <p
                                    @click="
                                        journeySummaryComponent.journeyKey = chosenOutboundJourney?.key;
                                        journeySummaryComponent.show = true;
                                    "
                                    class="text-decoration-underline cursor-pointer"
                                >
                                    {{ getFormattedDurationString(chosenOutboundJourney?.totalTimeIso) ?? "N/A" }}, {{ chosenOutboundJourney?.changes ?? 0 }} change{{ chosenOutboundJourney?.changes === 1 ? "" : "s" }}
                                </p>

                                <p @click="showTicketDetailsComponent('outbound')" class="text-decoration-underline cursor-pointer" :class="[journeyStore.getFareObjectByFareKey(journeyStore.selectedOutboundFares[0])?.value.displayName.length > 25 ? 'w-100 text-right' : '']">
                                    {{ journeyStore.getFareObjectByFareKey(journeyStore.selectedOutboundFares[0])?.value.displayName }}
                                </p>
                            </div>

                            <FirstClassTravelNotCompleteWarning :percentage="percentageOfOutboundTravelInFirstClass()" type="outbound"></FirstClassTravelNotCompleteWarning>

                            <p v-if="journeyStore.getFareObjectByFareKey(journeyStore.selectedOutboundFares[0])?.value.crossLondon" class="mt-1">✠ Fare includes cost of cross-London transfer</p>
                        </div>
                        <div v-if="chosenInboundJourney">
                            <v-divider class="my-3" opacity="0.6" thickness="1"></v-divider>

                            <div class="d-flex justify-start ga-1 mb-1">
                                <v-icon style="opacity: 0.8">mdi-arrow-left-box</v-icon>

                                <p>{{ chosenInboundJourney?.originName ?? "N/A" }} to {{ chosenInboundJourney?.destinationName ?? "N/A" }}</p>
                            </div>

                            <div class="d-flex justify-space-between flex-wrap">
                                <p
                                    @click="
                                        journeySummaryComponent.journeyKey = chosenInboundJourney?.key;
                                        journeySummaryComponent.show = true;
                                    "
                                    class="text-decoration-underline cursor-pointer"
                                >
                                    {{ getFormattedDurationString(chosenInboundJourney?.totalTimeIso) ?? "N/A" }}, {{ chosenInboundJourney?.changes ?? 0 }} change{{ chosenInboundJourney?.changes === 1 ? "" : "s" }}
                                </p>

                                <p @click="showTicketDetailsComponent('inbound')" class="text-decoration-underline cursor-pointer" :class="[journeyStore.getFareObjectByFareKey(journeyStore.selectedInboundFares[0])?.value.displayName.length > 25 ? 'w-100 text-right' : '']">
                                    {{ journeyStore.getFareObjectByFareKey(journeyStore.selectedInboundFares[0])?.value.displayName }}
                                </p>
                            </div>

                            <FirstClassTravelNotCompleteWarning :percentage="percentageOfInboundTravelInFirstClass()" type="inbound"></FirstClassTravelNotCompleteWarning>

                            <p v-if="journeyStore.getFareObjectByFareKey(journeyStore.selectedInboundFares[0])?.value.crossLondon" class="mt-1">✠ Fare includes cost of cross-London transfer</p>
                        </div>
                    </div>

                    <v-divider class="my-3"></v-divider>

                    <a @click="journeyStore.showOtherReturnFaresModal = true" class="d-block mt-3 text-center text-decoration-underline">View other fares</a>
                </template>
            </v-card-text>
        </v-card>

        <PriceBreakdown :price-breakdown="pricingBreakdown" v-if="showPricingBreakdown" @close="showPricingBreakdown = false"></PriceBreakdown>

        <JourneySummary v-if="journeySummaryComponent.show" :journeyKey="journeySummaryComponent.journeyKey" @close="journeySummaryComponent.show = false"></JourneySummary>

        <JourneyPackages v-if="journeyStore.showOtherReturnFaresModal" @close="journeyStore.showOtherReturnFaresModal = false"></JourneyPackages>

        <TicketDetails ref="td" :ticket-detail="ticketDetailsComponent.ticketDetails" :journey-fare="ticketDetailsComponent.fareDetails" :fare-type="ticketDetailsComponent.journeyType" :restrictionDetails="ticketDetailsComponent.restrictionDetails"></TicketDetails>
    </div>
</template>

<style scoped lang="scss">
@import "@/assets/base.scss";

.overview {
    height: fit-content;
}

.total-price-wrapper {
    display: flex;
    justify-content: space-between;
    font-size: 1.6rem;
    font-weight: 700;
    gap: 30px;
}

.total-price-wrapper span {
    font-weight: 700;
}

.button {
    background-color: var(--vt-c-orange);
}

.button :deep(.v-btn__content) {
    font-weight: bold;
    text-transform: capitalize;
    font-size: 1.3rem;
    letter-spacing: normal;
}

.pricing-breakdown-link {
    cursor: pointer;
    color: var(--vt-c-orange);
    text-decoration: underline;
    text-align: center;
}

.mobile-links {
    display: flex;
    justify-content: space-between;
    color: var(--vt-c-orange);
    font-size: 0.8rem;
}

.v-alert ul li {
    margin-left: 25px;
}

.railcard-discount-applied-text {
    margin-top: 2px;
    font-size: 0.8em;
    color: #555555;
    margin-bottom: -10px;
    text-align: right;
}
.railcard-discount-applied-text-mobile {
    margin-top: -1px;
    font-size: 0.57em;
    color: #555555;
    margin-bottom: -4px;
    text-align: right;
}
@media (min-width: $mobile-breakpoint) and (max-width: 1300px) {
    .ticket-selection-details {
        font-size: 0.8rem;
    }
}

@media screen and (max-width: $mobile-breakpoint) {
    .journey-selection-overview {
        position: fixed;
        width: 100vw;
        margin-left: -16px !important;
        bottom: 0;
        margin: 0;
        padding: 0;
        z-index: 99;
    }

    .journey-selection-overview .v-card {
        border-radius: 0 !important;
        box-shadow: 0px 0px 5px 0px rgb(165 165 165) !important;
    }

    .journey-selection-overview .v-card-text {
        padding: 5px 10px;
        border-radius: 0;
    }

    .total-price-wrapper {
        font-size: 1.3rem;
    }
}
</style>
